import React, { useEffect, useState } from "react";
import { api, ApiError } from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import CreateServiceInstanceForm from "./CreateServiceInstanceForm";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import AppDatePicker from "../../../components-ui/AppDatePicker";
import dayjs from "dayjs";
import ServiceInstanceRotaCard from "./ServiceInstanceRotaCard";

import "../../../../css/forms.css";
import "../../../../css/buttons.css";

/* =========================
 * Types
 * ========================= */

interface ServiceOption {
  serviceId: number;
  name: string;
}

interface ServiceInstanceDto {
  serviceInstanceId: number;
  serviceId: number;
  serviceName: string;
  serviceColorHex: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
}

/* =========================
 * Props
 * ========================= */

interface ServiceInstancesTabProps {
  tenantId: number;
  serviceId: number | null;
  onServiceSelected: (serviceId: number | null) => void;
}

/* =========================
 * Component
 * ========================= */

export default function ServiceInstancesTab({
  tenantId,
  serviceId,
  onServiceSelected,
}: ServiceInstancesTabProps) {
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [instances, setInstances] = useState<ServiceInstanceDto[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);

  const selectedService = services.find((s) => s.serviceId === serviceId);

  const today = new Date();

  const [fromDate, setFromDate] = useState(
    today.toISOString().substring(0, 10),
  );

  const [toDate, setToDate] = useState(
    new Date(today.setDate(today.getDate() + 30))
      .toISOString()
      .substring(0, 10),
  );

  /* =========================
   * Load services
   * ========================= */

  useEffect(() => {
    if (tenantId) {
      api<ServiceOption[]>(`/api/services?tenantId=${tenantId}`)
        .then(setServices)
        .catch(setError);
    }
  }, [tenantId]);

  /* =========================
   * Load service instances
   * ========================= */

  useEffect(() => {
    if (!serviceId) return;

    api<ServiceInstanceDto[]>("/api/ServicesCalendar/search", {
      method: "POST",
      body: {
        serviceIds: [serviceId],
        fromDate,
        toDate,
      },
    }).then((result) => {
      setInstances(result);
      if (selectedInstanceId && !result.some((item) => item.serviceInstanceId === selectedInstanceId)) {
        setSelectedInstanceId(null);
      }
    });
  }, [serviceId, fromDate, selectedInstanceId, toDate]);

  async function cancelSession(serviceInstanceId: number) {
    if (!window.confirm("Are you sure you want to cancel this session?"))
      return;

    try {
      await api<void>(`/api/serviceinstances/${serviceInstanceId}/cancel`, {
        method: "POST",
      });

      reloadSessions();
    } catch (err) {
      setError(err as ApiError);
    }
  }

  async function reinstateSession(serviceInstanceId: number) {
    if (!window.confirm("Reinstate this cancelled session?")) return;

    try {
      await api<void>(`/api/serviceinstances/${serviceInstanceId}/reinstate`, {
        method: "POST",
      });

      reloadSessions();
    } catch (err) {
      setError(err as ApiError);
    }
  }

  function reloadSessions() {
    api<ServiceInstanceDto[]>("/api/ServicesCalendar/search", {
      method: "POST",
      body: {
        serviceIds: serviceId ? [serviceId] : null,
        fromDate,
        toDate,
      },
    }).then(setInstances);
  }

  return (
    <>
      <PageHeader
        title="Create Schedule"
        subtitle="Create and manage service sessions."
        actions={
          serviceId ? (
            <button
              type="button"
              className="icon-button-solid icon-button-solid--danger"
              onClick={() => onServiceSelected(null)}
              title="Cancel service selection"
              aria-label="Cancel service selection"
            >
              <CloseIcon fontSize="small" />
            </button>
          ) : undefined
        }
      />

      <div className="card">
        {!serviceId && (
          <div className="form-group">
            <select
              defaultValue=""
              className="form-input"
              onChange={(e) => onServiceSelected(Number(e.target.value))}
            >
              <option value="" disabled>
                -- Choose a service --
              </option>
              {services.map((s) => (
                <option key={s.serviceId} value={s.serviceId}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {serviceId && (
          <div className="service-instances-layout">
            <div className="session-main">
              <div className="card">
                <div className="card-header-row">
                  <div>
                    <div className="card-title">Schedule Details</div>
                  </div>
                </div>

                {selectedService && (
                  <CreateServiceInstanceForm
                    serviceName={selectedService.name}
                    serviceId={serviceId}
                    onCreated={reloadSessions}
                  />
                )}
              </div>
            </div>

            <div className="session-sidebar">
              <div className="card date-filters">
                <div className="card-title">Upcoming Sessions</div>

              <div className="form-group">
                <label className="toggle-label-text">
                  <input
                    type="checkbox"
                    checked={showCancelled}
                    onChange={(e) => setShowCancelled(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Show cancelled sessions
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">From</label>
                <AppDatePicker
                  value={fromDate}
                  maxDate={dayjs(toDate)}
                  onChange={(value) => setFromDate(value ?? "")}
                />
              </div>

              <div className="form-group">
                <label className="form-label">To</label>
                <AppDatePicker
                  value={toDate}
                  minDate={dayjs(fromDate)}
                  onChange={(value) => setToDate(value ?? "")}
                />
              </div>
            </div>

            <div className="card upcoming-sessions">
              {error && <div className="error">{error.message}</div>}
              {instances.length === 0 && <div>No sessions scheduled.</div>}

              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {(showCancelled
                  ? instances
                  : instances.filter((i) => i.status !== "Cancelled")
                ).map((i) => (
                  <li
                    key={i.serviceInstanceId}
                    onClick={() => setSelectedInstanceId(i.serviceInstanceId)}
                    style={{
                      padding: "0.75rem 0",
                      borderBottom: "1px solid #eee",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 8,
                      cursor: "pointer",
                      background:
                        selectedInstanceId === i.serviceInstanceId
                          ? "#f8fafc"
                          : "transparent",
                    }}
                  >
                    <div>
                      <strong>
                        {new Date(i.startDateTime).toLocaleDateString()}
                      </strong>

                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        {new Date(i.startDateTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" - "}
                        {new Date(i.endDateTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color:
                            i.status === "Cancelled" ? "#9ca3af" : "#037d41",
                        }}
                      >
                        {i.status}
                      </div>
                    </div>

                    {i.status === "Cancelled" ? (
                      <button
                        type="button"
                        onClick={() => reinstateSession(i.serviceInstanceId)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#037d41",
                          padding: 4,
                        }}
                      >
                        <UndoIcon fontSize="small" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => cancelSession(i.serviceInstanceId)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#991b1b",
                          padding: 4,
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {serviceId && selectedInstanceId && (
              <ServiceInstanceRotaCard
                serviceId={serviceId}
                serviceInstanceId={selectedInstanceId}
              />
            )}
          </div>
        </div>
        )}
      </div>
    </>
  );
}
